import React from 'react';
import { tallyMarks, TALLY } from './tallytape';

/*
 * The /about page. Styled as paper, like the tape and the form, rather than as
 * a card floating on a page, so the whole site reads as one material.
 *
 * Every claim here about what the tool does not do is verified against the
 * current build. If you change what the app loads or stores, change this page
 * in the same commit.
 */

const mono = (size, color, extra) => ({
  fontFamily: 'var(--font-mono)',
  fontSize: `${size}px`,
  color,
  ...extra,
});

const sectionLabel = {
  ...mono(12, 'var(--ink)', {
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    fontWeight: 700,
    margin: '0 0 14px 0',
  }),
};

const body = {
  fontSize: '15px',
  color: 'var(--ink-soft)',
  lineHeight: 1.7,
  margin: '0 0 16px 0',
};

const link = {
  color: 'var(--accent-fixed)',
  textDecoration: 'none',
  fontWeight: 500,
};

/** The same mark the tape draws, so the illustration cannot drift from it. */
function tallySvg(count) {
  const g = TALLY.screenLg;
  const marks = tallyMarks(count, g);
  const pad = g.width;
  return (
    <svg
      width={marks.width + pad}
      height={g.h + pad}
      viewBox={`0 0 ${marks.width + pad} ${g.h + pad}`}
      aria-hidden="true"
      style={{ display: 'block', flex: '0 0 auto' }}
    >
      <g
        stroke="var(--accent-fixed)"
        strokeWidth={g.width}
        strokeLinecap="round"
      >
        {marks.segments.map(([x1, y1, x2, y2], i) => (
          <line
            key={i}
            x1={x1 + pad / 2}
            y1={y1 + pad / 2}
            x2={x2 + pad / 2}
            y2={y2 + pad / 2}
          />
        ))}
      </g>
    </svg>
  );
}

/** List items marked with a single tally stroke rather than a bullet. */
function TallyItem({ children }) {
  return (
    <li
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'baseline',
        marginBottom: '12px',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flex: '0 0 auto',
          width: '2px',
          height: '13px',
          borderRadius: '1px',
          background: 'var(--accent-fixed)',
          transform: 'translateY(2px)',
        }}
      />
      <span style={{ fontSize: '15px', color: 'var(--ink-soft)', lineHeight: 1.6 }}>
        {children}
      </span>
    </li>
  );
}

export default function About() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        padding: '48px 20px 64px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Header */}
      <div style={{ width: '100%', maxWidth: '700px', marginBottom: '40px' }}>
        <a
          href="/"
          className="about-back"
          style={{
            ...mono(12, 'var(--text-secondary)', {
              letterSpacing: '0.08em',
              textDecoration: 'none',
              display: 'inline-block',
              marginBottom: '32px',
            }),
          }}
        >
          {'←'} Back to TallyTape
        </a>

        <h1
          style={{
            fontSize: '40px',
            fontWeight: 600,
            margin: '0 0 16px 0',
            color: 'var(--text)',
            letterSpacing: '-0.02em',
          }}
        >
          About TallyTape
        </h1>
        <p
          style={{
            fontSize: '18px',
            color: 'var(--text-secondary)',
            margin: 0,
            lineHeight: 1.6,
          }}
        >
          A free invoice generator that prints a paper tally tape.
        </p>
      </div>

      {/* Paper */}
      <div
        style={{
          width: '100%',
          maxWidth: '700px',
          filter: 'drop-shadow(0 2px 12px rgba(40, 34, 28, 0.07))',
        }}
      >
        <div className="perf-paper about-paper" style={{ padding: '46px 44px' }}>
          <section>
            <h2 style={sectionLabel}>Why we made this</h2>
            <p style={body}>
              Budmen works with contractors, and those contractors needed a
              simple way to invoice us. Every tool we looked at wanted an
              account, a subscription, and a dashboard, all to produce a
              one-page document that says who did what and what it costs.
            </p>
            <p style={{ ...body, margin: 0 }}>
              So we built this instead, and left it free for anyone who has the
              same problem.
            </p>
          </section>

          <hr className="tape-rule" />

          <section>
            <h2 style={sectionLabel}>Why it looks like a receipt</h2>
            <p style={body}>
              The name promised a paper tally tape, so the invoice is one.
              Narrow measure, perforated along both edges, monospace
              throughout, dashed rules between the sections.
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                margin: '0 0 16px 0',
                padding: '18px 20px',
                background: 'var(--paper-shade)',
                borderRadius: '4px',
              }}
            >
              {tallySvg(7)}
              <span style={mono(13, 'var(--ink-soft)')}>7 x 45.00</span>
            </div>
            <p style={body}>
              Quantities count in fives because that is what a tally is: four
              uprights with a diagonal struck through them, then singles.
              Seven is a crossed group of five and two more. Past twelve the
              marks stop being countable and start reading as texture, so above
              that the numeral prints on its own.
            </p>
            <p style={{ ...body, margin: 0 }}>
              The PDF is drawn to match, centred on a standard US Letter page.
              A receipt-shaped page would look more striking and be worse, since
              invoices get printed, filed, and forwarded to bookkeepers who
              expect paper that fits the drawer.
            </p>
          </section>

          <hr className="tape-rule" />

          <section>
            <h2 style={sectionLabel}>How it works</h2>
            <p style={body}>
              TallyTape runs entirely in your browser. When you fill out an
              invoice, that information stays on your device. We use your
              browser's localStorage to save your work between sessions, and
              none of what you type is ever sent to our servers.
            </p>
            <p style={body}>
              When you generate a PDF, it is built on your device and downloaded
              directly. No middleman. No cloud storage. Nothing about the
              invoice is collected.
            </p>
            <p style={{ ...body, margin: 0 }}>
              The page itself makes no third-party requests. No webfonts, no
              scripts from a CDN, no analytics service. Your browser talks to
              tallytape.space and nothing else, which you are welcome to
              confirm in your browser's network tab. There is exactly one thing
              it sends there, and it is described below.
            </p>
          </section>

          <hr className="tape-rule" />

          <section>
            <h2 style={sectionLabel}>The one number we keep</h2>
            <p style={body}>
              We count how many invoices have been generated. That is a single
              integer, and it is the only thing that leaves your browser.
            </p>
            <p style={body}>
              The request carries no body. No invoice data, no name, no amount,
              no referrer, no session, no cookie, no identifier of any kind. It
              is an increment and nothing else. If you make ten invoices it
              counts ten, because there is nothing in it that could tell it was
              the same person twice, and nothing that tries.
            </p>
            <p style={body}>
              It is sent after your PDF has been built and handed to your
              browser, never before, and it is never in the way. If the request
              fails, or you block it, the invoice is already made and nothing on
              the page changes.
            </p>
            <p style={{ ...body, margin: 0 }}>
              The number is stored by Upstash, through Vercel, and what it
              holds is one number. That is the only third party anywhere in
              this stack, and we would rather name it here than have you find
              it later. It counts invoices, not people.
            </p>
          </section>

          <hr className="tape-rule" />

          <section>
            <h2 style={sectionLabel}>What we don't do</h2>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              <TallyItem>We don't require accounts or authentication</TallyItem>
              <TallyItem>
                We don't collect, store, or transmit your invoice data
              </TallyItem>
              <TallyItem>
                We don't run analytics or tracking pixels. We count how many
                invoices have been made, and nothing else: not who, not what,
                not from where
              </TallyItem>
              <TallyItem>
                We don't load anything from anyone else's servers
              </TallyItem>
              <TallyItem>
                We don't sell your data (we don't have it to sell)
              </TallyItem>
              <TallyItem>
                We don't send marketing emails (we don't have your email)
              </TallyItem>
              <TallyItem>
                TallyTape is free, and it stays free. There is no paid tier to
                graduate to.
              </TallyItem>
            </ul>
          </section>

          <hr className="tape-rule" />

          <section>
            <h2 style={sectionLabel}>Who made this</h2>
            <p style={body}>
              TallyTape is made by{' '}
              <a
                href="https://budmen.com"
                target="_blank"
                rel="noopener noreferrer"
                style={link}
                className="about-link"
              >
                Budmen Industries
              </a>
              , a curiosity-driven design and engineering studio in Syracuse,
              New York, led by Stephanie and Isaac Budmen. They make things,
              they teach, and they help organizations work through problems that
              do not have an answer yet.
            </p>
            <p style={body}>
              They also build their own tools when the ones on offer get in the
              way. There is more about that at{' '}
              <a
                href="https://budmen.com/tools"
                target="_blank"
                rel="noopener noreferrer"
                style={link}
                className="about-link"
              >
                budmen.com/tools
              </a>
              .
            </p>
            <p style={{ ...body, margin: 0 }}>
              This one is free with no strings attached. We made it because it
              needed to exist, not to grow a user base or collect data.
            </p>
          </section>

          <hr className="tape-rule" />

          <section>
            <h2 style={sectionLabel}>Use it however you want</h2>
            <p style={body}>
              TallyTape is provided as-is, free for anyone to use. We make no
              warranties about its fitness for any particular purpose, but we
              did our best to make it useful and reliable.
            </p>
            <p style={{ ...body, margin: 0 }}>
              If you find it helpful, share it with other independent makers who
              might benefit. That's all we ask.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          width: '100%',
          maxWidth: '700px',
          marginTop: '40px',
          textAlign: 'center',
        }}
      >
        <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', margin: 0 }}>
          Questions or feedback?{' '}
          <a
            href="https://budmen.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent)', textDecoration: 'none' }}
          >
            Get in touch
          </a>
        </p>
      </div>

      <style>{`
        .about-back:hover { color: var(--accent); }
        .about-link:hover { text-decoration: underline; }
        .about-paper section h2 { margin-top: 0; }
        @media (max-width: 560px) {
          .about-paper { padding: 36px 24px !important; }
        }
      `}</style>
    </div>
  );
}
